// Cascade-delete one patient form instance.
//
// A form instance owns two child tables — its field values and its signatures.
// Deleting only the instance would leave those orphaned: nothing references
// them any more, but they would still hold the patient's answers and signature
// strokes indefinitely.
//
// The cascade order mirrors `delete-encounter-service`, which already deletes
// these same three tables when a whole visit goes: CHILDREN FIRST, so an
// interruption never strands rows pointing at an instance that no longer
// exists.
//
// Writes are sequential `db.execute` calls, ordered so an interruption never
// strands a dangling reference. (PRD.329 correction: this comment used to say a
// `db.writeTransaction` would commit locally and never reach the server — the
// PRD.329 §9.3 spike disproved that; it uploads as one batch. Not migrated
// here. Inside a write transaction use only `tx.*`; `db.*` there deadlocks.)

/** Minimal shape of the PowerSync handle this service needs. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface DeleteFormInstanceParams {
  db: Db;
  formInstanceId: string;
}

export interface DeleteFormInstanceResult {
  deleted: boolean;
  error?: string;
}

export async function deleteFormInstanceCascade(
  params: DeleteFormInstanceParams,
): Promise<DeleteFormInstanceResult> {
  const { db, formInstanceId } = params;
  if (!formInstanceId) return { deleted: false, error: 'No form selected.' };

  try {
    // Children first.
    await db.execute(`DELETE FROM form_instance_values WHERE form_instance_id = ?`, [
      formInstanceId,
    ]);
    await db.execute(`DELETE FROM form_instance_signatures WHERE form_instance_id = ?`, [
      formInstanceId,
    ]);
    // Then the instance itself.
    await db.execute(`DELETE FROM form_instances WHERE id = ?`, [formInstanceId]);
    return { deleted: true };
  } catch (err) {
    return {
      deleted: false,
      error: err instanceof Error ? err.message : 'Could not delete this form.',
    };
  }
}

/**
 * The form DEFINITION is never touched.
 *
 * Deleting a patient's filled form removes that patient's copy only — the
 * template stays published for every other patient and every future visit.
 */
export const DELETE_FORM_INSTANCE_KEEPS_DEFINITION = true;
